import { Link2 } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { useSolutionDemandLinks } from "@/hooks/use-solution-demand-links";

/**
 * Solution Detail drawer → "Demand Link" tab. Read-only view of the NOS Demand(s)
 * linked to this Solution (auto-synced from the /demand-solution resolution — the
 * sync in Settings → Integrations is the only writer). A Solution's linked demands
 * scope its guidance forecast and hours actuals.
 */
export function SolutionDemandLinks({ solutionId }: { solutionId: string }) {
  const { data, isLoading, isError, error, refetch } = useSolutionDemandLinks(solutionId);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (isError) {
    return <ErrorState message={error.message} onRetry={() => refetch()} />;
  }

  const links = data?.links ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Link2 className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">NOS Demand Links</div>
          <p className="text-sm text-muted-foreground">
            Auto-synced from NOS. These demands scope this Solution's guidance forecast and hours
            actuals.
          </p>
        </div>
      </div>

      {links.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-6 text-center text-sm text-muted-foreground">
          No linked NOS demands. Run the demand sync in Settings → Integrations to link this
          Solution's demands.
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {links.length} linked NOS demand{links.length === 1 ? "" : "s"}.
          </p>
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
            {links.map((link) => (
              <li key={link.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {link.demandName ?? link.nymblDemandId}
                  </div>
                  <div className="truncate font-mono text-xs text-muted-foreground">
                    {link.nymblDemandId}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {link.lastSyncedAt
                    ? `synced ${new Date(link.lastSyncedAt).toLocaleDateString()}`
                    : "never synced"}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
